// Pure client-side filtering for the Users table (spec-4 ac-2). Every column is
// filterable and the filters compose with AND; "my users" is the owner==me cut
// (ac-5). Kept pure + separate from the page so the composition is unit-testable.
import type { UserRow } from "../api/types";

export interface UserFilters {
  search: string; // global: name / email / org / tags
  name: string;
  email: string;
  org: string;
  minSpecs: string;
  minDecisions: string;
  minAcs: string;
  mcp: "any" | "yes" | "no";
  tag: string;
  myUsers: boolean;
  hideInternal: boolean;
}

export const EMPTY_FILTERS: UserFilters = {
  search: "",
  name: "",
  email: "",
  org: "",
  minSpecs: "",
  minDecisions: "",
  minAcs: "",
  mcp: "any",
  tag: "",
  myUsers: false,
  hideInternal: false,
};

/** Whether `email` owns this user (an owner:<email> tag). Backs "my users". */
export function hasOwner(u: UserRow, email: string): boolean {
  return u.tags.some((t) => t.scope === "owner" && t.value === email);
}

function atLeast(raw: string, n: number): boolean {
  const min = parseInt(raw, 10);
  return !Number.isFinite(min) || n >= min;
}

const tagString = (u: UserRow) => u.tags.map((t) => `${t.scope}:${t.value}`).join(" ");
const orgString = (u: UserRow) => u.orgs.map((o) => o.orgName).join(" ");

export function filterUsers(users: UserRow[], f: UserFilters, myEmail: string): UserRow[] {
  const search = f.search.trim().toLowerCase();
  const name = f.name.trim().toLowerCase();
  const email = f.email.trim().toLowerCase();
  const org = f.org.trim().toLowerCase();
  const tag = f.tag.trim().toLowerCase();

  return users.filter((u) => {
    if (search) {
      const hay = `${u.name ?? ""} ${u.email} ${orgString(u)} ${tagString(u)}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    if (name && !(u.name ?? "").toLowerCase().includes(name)) return false;
    if (email && !u.email.toLowerCase().includes(email)) return false;
    if (org && !orgString(u).toLowerCase().includes(org)) return false;
    if (!atLeast(f.minSpecs, u.specCount)) return false;
    if (!atLeast(f.minDecisions, u.decisionCount)) return false;
    if (!atLeast(f.minAcs, u.acCount)) return false;
    if (f.mcp === "yes" && !u.mcpConnected) return false;
    if (f.mcp === "no" && u.mcpConnected) return false;
    if (tag && !tagString(u).toLowerCase().includes(tag)) return false;
    if (f.myUsers && !hasOwner(u, myEmail)) return false;
    if (f.hideInternal && u.isInternal) return false;
    return true;
  });
}
